import React, { useCallback, useEffect, useState } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalExportAbsences.module.sass";
import Icon from "../Icon";
import TextInput from "../TextInput";
import Card from "../Card";
import { exportAbsencesReport, exportAbsencesExcel } from "../../services/attendances.service";
import { showSuccess, showError } from "../../services/toast.service";

const ModalExportAbsences = ({ visible, onClose, partnerId, exportFormat = 'pdf' }) => {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [loading, setLoading] = useState(false);

    // Réinitialiser les dates quand la modal s'ouvre
    useEffect(() => {
        if (visible) {
            setStartDate("");
            setEndDate("");
        }
    }, [visible]);

    const escFunction = useCallback(
        (e) => {
            if (e.keyCode === 27) {
                onClose();
            }
        },
        [onClose]
    );

    const handleExport = async () => {
        if (!startDate || !endDate) {
            showError("Veuillez sélectionner une période complète");
            return;
        }

        if (new Date(startDate) > new Date(endDate)) {
            showError("La date de début doit être antérieure à la date de fin");
            return;
        }

        setLoading(true);
        try {
            if (exportFormat === 'excel') {
                await exportAbsencesExcel(partnerId, startDate, endDate);
                showSuccess("Rapport d'absences exporté en Excel avec succès");
            } else {
                await exportAbsencesReport(partnerId, startDate, endDate);
                showSuccess("Rapport d'absences exporté en PDF avec succès");
            }
            setTimeout(() => onClose(), 500);
        } catch (error) {
            console.error("Erreur lors de l'export:", error);
            showError(error.message || "Erreur lors de l'export du rapport");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);
        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    useEffect(() => {
        if (visible) {
            const target = document.querySelector("#modal-export-absences");
            disableBodyScroll(target);
        } else {
            clearAllBodyScrollLocks();
        }
    }, [visible]);

    return createPortal(
        visible && (
            <div id="modal-export-absences" className={styles.modal}>
                <div className={styles.control}>
                    <button className={styles.close} onClick={onClose}>
                        <Icon name="close" size="20" />
                    </button>
                </div>
                <div className={styles.outer} style={{ position: 'relative' }}>
                    <Card
                        className={cn(styles.card)}
                        title={`Exporter les absences en ${exportFormat === 'excel' ? 'Excel' : 'PDF'}`}
                        classTitle="title-blue"
                        style={{ minHeight: '300px', width: '600px', position: 'relative' }}
                    >
                        <div className={styles.description}>
                            <h3 style={{ marginBottom: '20px', fontSize: '16px' }}>
                                Sélectionnez une période pour exporter les absences :
                            </h3>
                            
                            <div className={styles.fieldGroup}>
                                <div className={styles.field}>
                                    <TextInput
                                        label="Date de début"
                                        name="startDate"
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <TextInput
                                        label="Date de fin"
                                        name="endDate"
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                            </div>
                            
                            <div className={styles.btnWrap} style={{ marginTop: '40px' }}>
                                <button 
                                    className="button-blue"
                                    onClick={handleExport}
                                    disabled={loading}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                                >
                                    {loading ? (
                                        <>
                                            <Icon name="loader" size="16" />
                                            Export en cours...
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="download" size="16" />
                                            Exporter en {exportFormat === 'excel' ? 'Excel' : 'PDF'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        ),
        document.body
    );
};

export default ModalExportAbsences;
